import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import services from '../../../../services'
import { formatDateTime } from '../../../../utils/format'
import { COMPLETED_STATUSES, MATCH_STATUS } from '../../../../utils/matchStatus'
import { getMatchSideForPlayer } from '../utils/playerHistorySelectors'

const getHistoryStatusLabel = (status) => {
  if (status === MATCH_STATUS.NORMAL) return 'Completed'
  if (status === MATCH_STATUS.SCHEDULED) return 'Scheduled'
  if (status === MATCH_STATUS.CREATED) return 'Unscheduled'
  if (status === MATCH_STATUS.UNPLAYED) return 'Not played'
  return status.replace('_', ' ')
}

const getMatchResultClass = (match, pid) => {
  if (!COMPLETED_STATUSES.has(match.status)) return 'text-muted'
  const outcome = services.match.getMatchOutcomeForPlayer(match, pid)
  if (outcome === 'win') return 'text-success fw-bold'
  if (outcome === 'loss') return 'text-danger'
  return 'text-muted'
}

const renderPlayerLink = (player, index, navigationState = undefined) => {
  const name =
    `${player?.first_name || ''} ${player?.last_name || ''}`.trim() ||
    `Player #${player?.id}`
  const playerId = Number(player?.id)

  return (
    <React.Fragment key={playerId || index}>
      {index > 0 && <span aria-hidden="true"> / </span>}
      {playerId ? (
        <Link
          to={`/players/${playerId}/history`}
          state={navigationState}
          className="text-decoration-none"
        >
          <strong>{name}</strong>
        </Link>
      ) : (
        <strong>{name}</strong>
      )}
    </React.Fragment>
  )
}

const getOpponentId = (match, pid) => {
  const { oppSide } = getMatchSideForPlayer(match, pid)
  if (oppSide.length === 1) return Number(oppSide[0]?.id) || null
  return null
}

const getPartnerPlayers = (match, pid) => {
  const { sameSide } = getMatchSideForPlayer(match, pid)
  return sameSide.filter((p) => Number(p?.id) !== Number(pid))
}

const getOpponentPlayers = (match, pid) =>
  getMatchSideForPlayer(match, pid).oppSide

export default function LeagueMatchesTable({
  matches,
  pid,
  isDoubles = false,
  partnerNavigationState = null,
}) {
  if (matches.length === 0) {
    return (
      <p className="text-muted text-center py-3">
        No matches in this league yet.
      </p>
    )
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0">
        <thead>
          <tr className="text-muted">
            <th className="border-0 fw-normal">Date</th>
            {isDoubles && <th className="border-0 fw-normal">Partner</th>}
            <th className="border-0 fw-normal">
              {isDoubles ? 'Opponents' : 'Opponent'}
            </th>
            <th className="border-0 fw-normal">Result</th>
            <th className="border-0 fw-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {[...matches]
            .sort(
              (a, b) =>
                new Date(b.match_datetime || b.created_at) -
                new Date(a.match_datetime || a.created_at),
            )
            .map((match) => {
              const partnerPlayers = isDoubles
                ? getPartnerPlayers(match, pid)
                : []
              const opponentPlayers = isDoubles
                ? getOpponentPlayers(match, pid)
                : []
              const opponentId = isDoubles ? null : getOpponentId(match, pid)

              return (
                <tr key={match.id}>
                  <td data-label="Date">
                    {match.match_datetime
                      ? formatDateTime(match.match_datetime, {
                          includeYear: false,
                        })
                      : 'Not scheduled'}
                  </td>
                  {isDoubles && (
                    <td data-label="Partner">
                      {partnerPlayers.length > 0 ? (
                        partnerPlayers.map((p, i) =>
                          renderPlayerLink(
                            p,
                            i,
                            partnerNavigationState || undefined,
                          ),
                        )
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )}
                  <td data-label={isDoubles ? 'Opponents' : 'Opponent'}>
                    {isDoubles ? (
                      opponentPlayers.length > 0 ? (
                        opponentPlayers.map((p, i) => renderPlayerLink(p, i))
                      ) : (
                        <span className="text-muted">—</span>
                      )
                    ) : opponentId ? (
                      <Link
                        to={`/players/${opponentId}/history`}
                        className="text-decoration-none"
                      >
                        <strong>
                          {services.match.getOpponentName(match, pid)}
                        </strong>
                      </Link>
                    ) : (
                      <strong>
                        {services.match.getOpponentName(match, pid)}
                      </strong>
                    )}
                  </td>
                  <td
                    data-label={
                      COMPLETED_STATUSES.has(match.status)
                        ? 'Result'
                        : undefined
                    }
                    className={getMatchResultClass(match, pid)}
                  >
                    {COMPLETED_STATUSES.has(match.status)
                      ? services.match.formatMatchScore(match, pid)
                      : '—'}
                  </td>
                  <td data-label="Status">
                    <span
                      className={`badge ${
                        COMPLETED_STATUSES.has(match.status)
                          ? 'bg-success'
                          : match.status === MATCH_STATUS.SCHEDULED
                            ? 'bg-warning text-dark'
                            : 'bg-secondary'
                      }`}
                    >
                      {getHistoryStatusLabel(match.status)}
                    </span>
                  </td>
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )
}

LeagueMatchesTable.propTypes = {
  matches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      home_player_id: PropTypes.number,
      away_player_id: PropTypes.number,
      home_first_name: PropTypes.string,
      home_last_name: PropTypes.string,
      away_first_name: PropTypes.string,
      away_last_name: PropTypes.string,
      match_datetime: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      created_at: PropTypes.string,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
  pid: PropTypes.number.isRequired,
  isDoubles: PropTypes.bool,
  partnerNavigationState: PropTypes.shape({
    fromLeague: PropTypes.bool,
    leagueId: PropTypes.number,
    divisionId: PropTypes.number,
  }),
}
